
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ExpenseDetailsDialogProps {
  expenseId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

// Define interface for the expense data including the optional house_name
interface ExpenseWithDetails {
  id: string;
  amount: number;
  description: string | null;
  expense_date: string;
  entity_id: string;
  entity_type: string;
  receipt_url: string | null;
  expense_categories: {
    name: string;
  };
  house_name?: string; // Optional house name property
}

export const ExpenseDetailsDialog = ({ expenseId, isOpen, onClose }: ExpenseDetailsDialogProps) => {
  const { data: expense, isLoading } = useQuery({
    queryKey: ['expense-details', expenseId],
    queryFn: async () => {
      if (!expenseId) return null;
      
      // Modify the query to fetch data properly
      const { data, error } = await supabase
        .from('expenses')
        .select(`
          *,
          expense_categories (
            name
          )
        `)
        .eq('id', expenseId)
        .single();
      
      if (error) throw error;
      
      // Get house data in a separate query if needed
      if (data.entity_type === 'house' && data.entity_id) {
        const { data: houseData, error: houseError } = await supabase
          .from('houses')
          .select('name')
          .eq('id', data.entity_id)
          .single();
          
        if (!houseError && houseData) {
          // Attach house data to the expense object
          return { ...data, house_name: houseData.name } as ExpenseWithDetails;
        }
      }
      
      return data as ExpenseWithDetails;
    },
    enabled: isOpen && !!expenseId,
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Detalles del gasto</DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : expense ? (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Categoría</p>
              <p className="font-medium">{expense.expense_categories?.name}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Monto</p>
              <p className="font-medium">${expense.amount.toLocaleString('es-CL')}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Fecha</p>
              <p className="font-medium">
                {format(parseISO(expense.expense_date), "d 'de' MMMM 'de' yyyy", { locale: es })}
              </p>
            </div>
            
            {expense.description && (
              <div>
                <p className="text-sm text-gray-500">Descripción</p>
                <p className="font-medium">{expense.description}</p>
              </div>
            )}
            
            {expense.house_name && (
              <div>
                <p className="text-sm text-gray-500">Casa</p>
                <p className="font-medium">{expense.house_name}</p>
              </div>
            )}
            
            {expense.receipt_url && (
              <div>
                <p className="text-sm text-gray-500">Comprobante</p>
                <a 
                  href={expense.receipt_url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Ver comprobante
                </a>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-gray-500">No se encontró información del gasto</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
